import { Children, cloneElement, isValidElement, useId, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { cx } from '@/shared/ui/cx';

export type TooltipProps = {
  content: ReactNode;
  placement?: 'top' | 'right' | 'bottom' | 'left';
  /** Set when the trigger already has an accessible name of its own. */
  decorative?: boolean;
  /**
   * Renders the anchor but never shows the label.
   *
   * For callers that turn a tooltip on and off while the *same* control stays
   * on screen — the sidebar's nav items and user chip, which are tipped only
   * while the rail is a narrow strip. Conditionally rendering `<Tooltip>` around
   * them instead changes the element type at that position, so React unmounts
   * and remounts the subtree: the sidebar's user menu lost its open state
   * mid-click, because focusing the chip is what widened the rail.
   *
   * The anchor drops to `display: contents` here so an inline-flex wrapper
   * cannot shrink a full-width nav item.
   */
  disabled?: boolean;
  className?: string;
  children: ReactNode;
};

/**
 * The dark label on the collapsed sidebar icons (`Open sidebar`, `Search`, …).
 *
 * Shows on hover *and* on keyboard focus — a hover-only tooltip is invisible to
 * anyone tabbing through the rail. When the trigger is an icon button that
 * already carries an `aria-label`, pass `decorative` so the text is not
 * announced twice.
 */
export function Tooltip({
  content,
  placement = 'right',
  decorative = false,
  disabled = false,
  className,
  children,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const showing = open && !disabled;

  /* The description has to sit on the control itself: `aria-describedby` on a
     wrapper <span> that merely *contains* the button associates nothing, so a
     non-decorative tooltip rendered fine and was never announced. When the
     child is a single element — every current caller — the attribute is cloned
     onto it while the tip is showing (the standard tooltip timing: it appears
     on the same focus that would have it read). A fragment or text child has no
     single control to describe; the wrapper attribute stays as a best effort,
     and such a trigger should be passing `decorative` anyway. */
  const describedBy = !decorative && showing ? id : undefined;
  const singleChild =
    describedBy && Children.count(children) === 1 && isValidElement(children)
      ? (children as ReactElement<{ 'aria-describedby'?: string }>)
      : null;
  const trigger = singleChild
    ? cloneElement(singleChild, { 'aria-describedby': describedBy })
    : children;

  return (
    <span
      className={cx('tooltip-anchor', disabled && 'tooltip-anchor--off', className)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      aria-describedby={singleChild ? undefined : describedBy}
    >
      {trigger}
      {showing ? (
        <span
          id={id}
          role={decorative ? undefined : 'tooltip'}
          aria-hidden={decorative || undefined}
          className={cx('tooltip', `tooltip--${placement}`)}
        >
          {content}
        </span>
      ) : null}
    </span>
  );
}
